/** Typed presentation data. Views assign these values to component props. */
import { defineStore } from "pinia";
import { computed } from "vue";
import { useWeatherStore } from "./weather.store";
import { useSettingsStore } from "./settings.store";
import { useUnitConversion } from "@/composables/useUnitConversion";
import type { HourlyDataPoint, DayForecast } from "@/models/forecast";

export const useForecastStore = defineStore("forecast", () => {
  const weatherStore = useWeatherStore();
  const settings = useSettingsStore();
  const { convertTemperature } = useUnitConversion(
    () => settings.temperatureUnit,
  );

  // Project hourly weather into chart props in the selected location's timezone.
  const hourlyForecast = computed<HourlyDataPoint[]>(() => {
    const hourly = weatherStore.hourlyWeather;
    if (!hourly?.hours || hourly.hours.length === 0) {
      return [];
    }

    // Get the timezone offset for the location (must be in computed scope for reactivity)
    const offsetSeconds =
      weatherStore.currentWeather?.timeZoneOffsetFromGMT ?? 0;

    return hourly.hours.slice(0, 192).map((hour, idx) => ({
      time:
        idx === chartCurrentIndex.value
          ? "NOW"
          : formatHourWithDay(hour.startTime.toString()),
      temp: convertTemperature(hour.temperature) ?? 0,
      weatherCode: hour.weatherCode,
      pop: hour.pop ?? 0,
      isDay: hour.isDay,
      windSpeed: Math.round(hour.windSpeed),
      windDirection: hour.windBearing,
      rainRate: hour.rainRate,
      snowRate: hour.snowRate,
      iceRate: hour.iceRate,
      forecastDayIndex: getForecastDayIndex(hour.startTime, offsetSeconds),
    }));
  });

  // Shared currentIndex compares UTC now with UTC forecast-hour boundaries.
  const chartCurrentIndex = computed(() => {
    return weatherStore.hourlyWeather?.currentIndex ?? 0;
  });

  // Pair day/night periods into the cards used by every forecast layout.
  const weeklyForecast = computed<DayForecast[]>(() => {
    const daily = weatherStore.dailyWeather;
    if (!daily?.days || daily.days.length === 0) {
      return [];
    }

    const combinedDays: DayForecast[] = [];

    // Process periods and combine day/night pairs
    let i = 0;
    while (i < daily.days.length && combinedDays.length < 8) {
      const period = daily.days[i];

      if (period.isDay) {
        // This is a daytime period - look for matching night period
        const nightPeriod =
          daily.days[i + 1]?.isDay === false ? daily.days[i + 1] : null;

        const calendarDayIndex = getForecastDayIndex(
          period.startTime,
          weatherStore.currentWeather?.timeZoneOffsetFromGMT ?? 0,
        );
        combinedDays.push({
          calendarDayIndex,
          day:
            calendarDayIndex === 0
              ? "Today"
              : period.nameShort || period.name || "",
          high: convertTemperature(period.maxTemperature) ?? 0,
          low:
            convertTemperature(
              nightPeriod?.minTemperature ?? period.minTemperature,
            ) ?? 0,
          dayWeatherCode: period.weatherCode,
          nightWeatherCode: nightPeriod?.weatherCode ?? period.weatherCode,
          dayCondition: period.shortForecast || "Weather",
          nightCondition:
            nightPeriod?.shortForecast || period.shortForecast || "Weather",
          dayPop: period.precipChance,
          nightPop: nightPeriod?.precipChance,
          dayRain: period.rainAccumulation,
          daySnow: period.snowAccumulation,
          dayIce: period.iceAccumulation,
          nightRain: nightPeriod?.rainAccumulation,
          nightSnow: nightPeriod?.snowAccumulation,
          nightIce: nightPeriod?.iceAccumulation,
        });

        // Skip both day and night periods
        i += nightPeriod ? 2 : 1;
      } else {
        // Night period without a preceding day (e.g., starting in evening)
        // Day cards start with the next daytime period, retaining its calendar index.
        i++;
      }
    }

    return combinedDays;
  });

  /** Convert a UTC forecast instant to the selected location's display time. */
  function toLocationTime(date: Date): Date {
    const utcMs = date.getTime() + date.getTimezoneOffset() * 60 * 1000;
    const offsetSeconds =
      weatherStore.currentWeather?.timeZoneOffsetFromGMT ?? 0;
    return new Date(utcMs + offsetSeconds * 1000);
  }

  /** Return the location-local day offset for a forecast period. */
  function getForecastDayIndex(date: Date, offsetSeconds: number): number {
    const now = new Date();

    // Extract local calendar parts through UTC accessors, independent of browser timezone.
    const getLocationDate = (d: Date) => {
      const locationMs = d.getTime() + offsetSeconds * 1000;
      const shifted = new Date(locationMs);
      return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth(),
        day: shifted.getUTCDate(),
      };
    };

    const nowLocal = getLocationDate(now);
    const dateLocal = getLocationDate(date);

    // Same calendar day?
    if (
      dateLocal.year === nowLocal.year &&
      dateLocal.month === nowLocal.month &&
      dateLocal.day === nowLocal.day
    ) {
      return 0;
    }

    // Create midnight timestamps in UTC for comparison
    const todayMidnightUTC = Date.UTC(
      nowLocal.year,
      nowLocal.month,
      nowLocal.day,
    );
    const targetMidnightUTC = Date.UTC(
      dateLocal.year,
      dateLocal.month,
      dateLocal.day,
    );

    const dayDiff = Math.round(
      (targetMidnightUTC - todayMidnightUTC) / (24 * 60 * 60 * 1000),
    );

    return Math.max(0, dayDiff);
  }

  /** Label an hourly point with its location-local day and hour. */
  function formatHourWithDay(dateString: string): string {
    const localDate = toLocationTime(new Date(dateString));
    const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const dayAbbr = dayNames[localDate.getDay()];
    const hour = localDate.getHours();

    if (hour === 0) return `${dayAbbr} 12AM`;
    if (hour === 12) return `${dayAbbr} 12PM`;
    if (hour < 12) return `${dayAbbr} ${hour}AM`;
    return `${dayAbbr} ${hour - 12}PM`;
  }

  return { hourlyForecast, weeklyForecast, chartCurrentIndex };
});
